import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Package, Search, Filter, Grid, List, ChevronLeft, ChevronRight, MapPin, Shield, Star, CheckCircle, Award, Send } from 'lucide-react';
import { productsApi, categoriesApi, countriesApi } from '../services/api';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);

  const page = parseInt(searchParams.get('page') || '1');
  const search = searchParams.get('search') || '';
  const industry = searchParams.get('industry') || '';
  const country = searchParams.get('country') || '';
  const sort = searchParams.get('sort') || 'newest';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes, categoriesRes, countriesRes] = await Promise.all([
          productsApi.getAll({ page, search, industry, country, sort, limit: 12 }),
          categoriesApi.getAll(),
          countriesApi.getAll()
        ]);
        setProducts(productsRes.data.products || []);
        setTotalPages(productsRes.data.pages || 1);
        setTotal(productsRes.data.total || 0);
        setCategories(categoriesRes.data || []);
        setCountries(countriesRes.data || []);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [page, search, industry, country, sort]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const form = e.target;
    const searchValue = form.search.value;
    updateFilter('search', searchValue);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />

      {/* Hero Banner */}
      <section className="bg-[#0A192F] py-12">
        <div className="container-main">
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight">Browse Products</h1>
          <p className="text-slate-300 max-w-2xl">
            Discover quality industrial products from verified suppliers worldwide
          </p>
        </div>
      </section>

      <div className="container-main py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className="w-full lg:w-64 flex-shrink-0">
            <div className="bg-white border border-slate-200 rounded-xl p-6 sticky top-24">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filters
              </h3>

              {/* Search */}
              <form onSubmit={handleSearch} className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    name="search"
                    placeholder="Search products..."
                    defaultValue={search}
                    className="pl-9"
                    data-testid="products-search-input"
                  />
                </div>
              </form>

              {/* Category Filter */}
              <div className="mb-6">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Category</label>
                <Select value={industry || 'all'} onValueChange={(val) => updateFilter('industry', val === 'all' ? '' : val)}>
                  <SelectTrigger data-testid="products-category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Country Filter */}
              <div className="mb-6">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Origin Country</label>
                <Select value={country || 'all'} onValueChange={(val) => updateFilter('country', val === 'all' ? '' : val)}>
                  <SelectTrigger data-testid="products-country-filter">
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {countries.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Filters */}
              {(search || industry || country) && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setSearchParams({})}
                  data-testid="products-clear-filters"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </aside>

          {/* Products Grid */}
          <main className="flex-1">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <p className="text-slate-600">
                Showing <span className="font-semibold text-slate-900">{total}</span> products
              </p>
              <div className="flex items-center gap-4">
                <Select value={sort} onValueChange={(val) => updateFilter('sort', val)}>
                  <SelectTrigger className="w-[180px]" data-testid="products-sort">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="price_low">Price: Low to High</SelectItem>
                    <SelectItem value="price_high">Price: High to Low</SelectItem>
                    <SelectItem value="popular">Most Popular</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    className={`p-2 ${viewMode === 'grid' ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid className="w-5 h-5 text-slate-600" />
                  </button>
                  <button
                    className={`p-2 ${viewMode === 'list' ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                    onClick={() => setViewMode('list')}
                  >
                    <List className="w-5 h-5 text-slate-600" />
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className={`grid ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-6`}>
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white border rounded-xl overflow-hidden animate-pulse">
                    <div className="h-48 bg-slate-200"></div>
                    <div className="p-4">
                      <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No products found</h3>
                <p className="text-slate-600">Try adjusting your filters or search terms</p>
              </div>
            ) : (
              <>
                <div className={`grid ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-6`}>
                  {products.map((product) => (
                    <Link
                      key={product.product_id}
                      to={`/products/${product.product_id}`}
                      className={`bg-white border border-slate-200 overflow-hidden hover:shadow-lg hover:border-[#0A192F]/20 hover:-translate-y-1 transition-all duration-200 group ${viewMode === 'list' ? 'flex' : ''}`}
                      data-testid={`product-item-${product.product_id}`}
                    >
                      <div className={`relative ${viewMode === 'list' ? 'w-48 flex-shrink-0' : ''} h-52 bg-slate-100 overflow-hidden`}>
                        {product.images?.[0] ? (
                          <img 
                            src={product.images[0]} 
                            alt={product.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
                            <Package className="w-14 h-14 text-slate-300" />
                          </div>
                        )}
                        {/* Supplier verified badge */}
                        {product.supplier_verified && (
                          <div className="absolute top-2 left-2">
                            <Badge className="bg-blue-600 text-white text-[10px] border-0 shadow-sm">
                              <CheckCircle className="w-3 h-3 mr-0.5" /> Verified
                            </Badge>
                          </div>
                        )}
                        {product.samples_available && (
                          <div className="absolute top-2 right-2">
                            <Badge className="bg-green-600 text-white text-[10px] border-0 shadow-sm">Samples</Badge>
                          </div>
                        )}
                      </div>
                      <div className="p-4 flex-1 flex flex-col">
                        <h3 className="font-semibold text-[#0A192F] text-sm line-clamp-2 group-hover:text-[#EA580C] transition-colors leading-snug mb-2">
                          {product.title}
                        </h3>
                        <div className="text-[#EA580C] font-bold text-base mb-2">
                          ${product.price_min?.toFixed(2)}{product.price_max ? ` - $${product.price_max.toFixed(2)}` : ''}
                          <span className="text-xs text-slate-400 font-normal ml-1">/ {product.moq_unit || 'piece'}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          <span className="text-[10px] bg-[#0A192F] text-white px-2 py-0.5 font-medium">
                            MOQ: {product.moq} {product.moq_unit}
                          </span>
                          {product.origin_country && (
                            <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 font-medium flex items-center gap-0.5">
                              <MapPin className="w-2.5 h-2.5" /> {product.origin_country}
                            </span>
                          )}
                          {product.certifications?.length > 0 && (
                            <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 font-medium flex items-center gap-0.5">
                              <Award className="w-2.5 h-2.5" /> {product.certifications[0]}
                            </span>
                          )}
                        </div>
                        <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between">
                          <span className="text-xs text-slate-500 truncate">{product.supplier_name || 'Verified Supplier'}</span>
                          <span className="text-xs text-[#EA580C] font-semibold flex items-center gap-1 shrink-0">
                            <Send className="w-3 h-3" /> Inquire
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-8">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => updateFilter('page', String(page - 1))}
                      data-testid="products-prev-page"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === totalPages}
                      onClick={() => updateFilter('page', String(page + 1))}
                      data-testid="products-next-page"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductsPage;
